import type { PimFields, Variant } from "@thenounproject/lingo-core";
import { v4 as genUuid } from "uuid";
import { withSource, isOverride } from "../productTypeFields";

export type ProductOptionData = {
  id: string;
  name: string;
  values: string[];
};

export type LocalVariant = {
  key: string;
  uuid?: string;
  optionValues: Record<string, string>;
  marketUuid: string | null;
  /** Full field value map. `source` marks each entry as "override" or "inherited". */
  fields: PimFields;
  displayOrder: number;
  status?: string;
};

/**
 * Generate a stable key for a variant based on its option values and market.
 * Used for React keys and override lookup.
 */
export function createVariantKey(
  optionValues: Record<string, string>,
  marketUuid: string | null
): string {
  const optionStr = Object.keys(optionValues)
    .sort()
    .map(k => `${k}:${optionValues[k]}`)
    .join("|");
  return `${optionStr}||${marketUuid || "none"}`;
}

/**
 * Generate the cartesian product of all option values.
 * e.g., [{ name: "Size", values: ["500mg", "1g"] }] → [{ Size: "500mg" }, { Size: "1g" }]
 */
export function generateOptionCombinations(options: ProductOptionData[]): Record<string, string>[] {
  const valid = options.filter(o => o.name.trim() && o.values.some(v => v.trim()));
  if (!valid.length) return [];
  return valid.reduce<Record<string, string>[]>(
    (acc, opt) => {
      const trimmed = opt.values.filter(v => v.trim());
      return acc.flatMap(combo => trimmed.map(v => ({ ...combo, [opt.name.trim()]: v.trim() })));
    },
    [{}]
  );
}

/** Seed a variant's fields by copying the product's fields and marking every field value inherited. */
function seedInheritedFields(productFields: PimFields): PimFields {
  const out: PimFields = {};
  for (const [id, v] of Object.entries(productFields)) {
    out[id] = withSource(v, "inherited");
  }
  return out;
}

/**
 * Find the unique existing variant to carry overrides from when an option is added or deleted.
 *
 * Add case: existing ⊆ target (e.g. existing {Size:small} → new {Size:small, Color:blue}).
 * Delete case: target ⊆ existing (e.g. existing {Size:small, Color:blue} → new {Size:small}).
 *
 * A defaults-only variant (empty optionValues) is vacuously a subset of any target.
 * If multiple candidates exist, returns undefined — ambiguous merges are worse than a clean
 * reset to inherited.
 */
function findUnambiguousPartialMatch(
  existingVariants: LocalVariant[],
  targetOptionValues: Record<string, string>,
  marketUuid: string | null
): LocalVariant | undefined {
  const targetEntries = Object.entries(targetOptionValues);
  const candidates = existingVariants.filter(v => {
    if (v.marketUuid !== marketUuid) return false;
    const vEntries = Object.entries(v.optionValues);
    const existingSubsetOfTarget = vEntries.every(([k, val]) => targetOptionValues[k] === val);
    const targetSubsetOfExisting = targetEntries.every(([k, val]) => v.optionValues[k] === val);
    return existingSubsetOfTarget || targetSubsetOfExisting;
  });
  // Only carry overrides when there is exactly one source — unambiguous expand (add) case.
  // Multiple candidates means two existing variants are both subsets of the target (e.g. adding
  // two dimensions at once); reset to inherited rather than pick arbitrarily.
  return candidates.length === 1 ? candidates[0] : undefined;
}

/** Seed inherited fields from product, then overlay any override fields from a partial match. */
function mergeOverridesFromPartialMatch(
  partialMatch: LocalVariant,
  productFields: PimFields
): PimFields {
  const fields = seedInheritedFields(productFields);
  for (const [id, v] of Object.entries(partialMatch.fields)) {
    if (isOverride(v)) fields[id] = v;
  }
  return fields;
}

/**
 * Build the full variant matrix from options × markets.
 * Preserves existing overrides and UUIDs when regenerating.
 */
export function buildVariantMatrix(
  options: ProductOptionData[],
  assignedMarketUuids: string[],
  existingVariants: LocalVariant[],
  existingApiVariants?: Variant[],
  productFields: PimFields = {}
): LocalVariant[] {
  const combinations = generateOptionCombinations(options);
  const hasMarkets = assignedMarketUuids.length > 0;
  if (!combinations.length && !hasMarkets) return [];

  const effectiveCombinations = combinations.length ? combinations : [{}];
  const marketTargets: (string | null)[] = hasMarkets ? assignedMarketUuids : [null];

  const existingByKey = new Map<string, LocalVariant>(existingVariants.map(v => [v.key, v]));
  const apiByKey = new Map<string, Variant>(
    (existingApiVariants ?? []).map(v => [createVariantKey(v.optionValues ?? {}, v.marketUuid), v])
  );

  const result: LocalVariant[] = [];
  let displayOrder = 0;

  for (const marketUuid of marketTargets) {
    for (const optionValues of effectiveCombinations) {
      const key = createVariantKey(optionValues, marketUuid);
      const existing = existingByKey.get(key);

      if (existing) {
        result.push({ ...existing, displayOrder: displayOrder++ });
        continue;
      }

      const apiVariant = apiByKey.get(key);
      const partialMatch = !apiVariant
        ? findUnambiguousPartialMatch(existingVariants, optionValues, marketUuid)
        : undefined;

      result.push({
        key,
        uuid: apiVariant?.uuid,
        optionValues,
        marketUuid,
        fields: partialMatch
          ? mergeOverridesFromPartialMatch(partialMatch, productFields)
          : (apiVariant?.fields ?? seedInheritedFields(productFields)),
        displayOrder: displayOrder++,
        status: apiVariant?.status,
      });
    }
  }

  return result;
}

/**
 * Build a map from old option values to new option values for positions where a value
 * was renamed (replaced by a different value at the same index).
 * A rename is detected when old[i] != new[i], old[i] is absent from the new set,
 * and new[i] is absent from the old set — distinguishing renames from reorders.
 */
export function buildOptionValueRenameMap(
  oldValues: string[],
  newValues: string[]
): Map<string, string> {
  const oldSet = new Set(oldValues.map(v => v.trim()));
  const newSet = new Set(newValues.map(v => v.trim()));
  const remap = new Map<string, string>();
  for (let i = 0; i < Math.min(oldValues.length, newValues.length); i++) {
    const old = oldValues[i].trim(),
      next = newValues[i].trim();
    if (old === next || !old || !next) continue;
    if (!newSet.has(old) && !oldSet.has(next)) remap.set(old, next);
  }
  return remap;
}

/**
 * Remap variant optionValues and keys after an option rename (name or values).
 * Must be called before buildVariantMatrix so key-based lookup still finds
 * existing variants with their overrides intact.
 */
export function remapVariantsForOptionRename(
  variants: LocalVariant[],
  oldOption: ProductOptionData,
  newOption: ProductOptionData
): LocalVariant[] {
  const oldName = oldOption.name.trim(),
    newName = newOption.name.trim();
  const valueRenames = buildOptionValueRenameMap(oldOption.values, newOption.values);
  if (oldName === newName && valueRenames.size === 0) return variants;
  return variants.map(v => {
    if (!(oldName in v.optionValues)) return v;
    const newOptionValues = { ...v.optionValues };
    const val = newOptionValues[oldName];
    delete newOptionValues[oldName];
    if (newName) newOptionValues[newName] = valueRenames.get(val) ?? val;
    return {
      ...v,
      key: createVariantKey(newOptionValues, v.marketUuid),
      optionValues: newOptionValues,
    };
  });
}

/**
 * Extract unique option definitions from existing API variants.
 */
export function extractOptionsFromVariants(variants: Variant[]): ProductOptionData[] {
  if (!variants?.length) return [];
  const optionMap = new Map<string, Set<string>>();
  variants.forEach(v => {
    Object.entries(v.optionValues || {}).forEach(([name, value]) => {
      if (!optionMap.has(name)) optionMap.set(name, new Set());
      optionMap.get(name)!.add(String(value));
    });
  });
  return Array.from(optionMap.entries()).map(([name, values]) => ({
    id: genUuid(),
    name,
    values: Array.from(values),
  }));
}

/**
 * Extract unique assigned market UUIDs from existing API variants.
 */
export function extractMarketUuidsFromVariants(variants: Variant[]): string[] {
  if (!variants?.length) return [];
  return [...new Set(variants.flatMap(v => (v.marketUuid ? [v.marketUuid] : [])))];
}

/**
 * Format a variant's option values into a display label.
 */
export function formatVariantLabel(optionValues: Record<string, string>): string {
  const values = Object.values(optionValues);
  return values.join(" / ") || "Defaults";
}
